import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth'
import { collection, doc, getDoc, setDoc } from 'firebase/firestore'
import { setAuthPlaceId } from '../lib/authPlaceStore'
import { clearPlaceStorage } from '../lib/placeStorage'
import { bootstrapPlace } from '../lib/bootstrapPlace'
import { getFirebaseAuth } from '../lib/firebase'
import { getFirestoreInstance, useFirestore } from '../lib/firebase'
import { clearAllCaches } from '../lib/firestore'

export interface UserProfile {
  defaultPlaceId: string
  companyId: string
  name: string
  email: string
  position: string
}

export interface RegisterInput {
  name: string
  email: string
  position: string
  companyName: string
  placeName: string
  address: string
  password: string
}

interface AuthContextValue {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  /** Set when Firebase Auth config is missing or invalid (e.g. auth/configuration-not-found). */
  authError: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  register: (input: RegisterInput) => Promise<string>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue | null {
  return useContext(AuthContext)
}

const CONFIG_NOT_FOUND = 'auth/configuration-not-found'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)

  const auth = getFirebaseAuth()

  useEffect(() => {
    if (!auth) {
      setLoading(false)
      return
    }
    setAuthError(null)
    let unsubscribe: (() => void) | undefined
    try {
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        setUser(firebaseUser)
        if (!firebaseUser) {
          setUserProfile(null)
          setAuthPlaceId(null)
          clearPlaceStorage()
          clearAllCaches()
          setLoading(false)
          return
        }
        if (useFirestore()) {
          try {
            const db = getFirestoreInstance()
            const profileSnap = await getDoc(doc(db, 'users', firebaseUser.uid))
            if (profileSnap.exists()) {
              const data = profileSnap.data() as UserProfile
              setUserProfile(data)
              setAuthPlaceId(data.defaultPlaceId)
            } else {
              setUserProfile(null)
              setAuthPlaceId(null)
            }
          } catch {
            setUserProfile(null)
            setAuthPlaceId(null)
          }
        } else {
          setUserProfile(null)
          setAuthPlaceId(null)
        }
        setLoading(false)
      })
    } catch (err: unknown) {
      const code = err && typeof err === 'object' && 'code' in err ? (err as { code: string }).code : ''
      if (code === CONFIG_NOT_FOUND) {
        setAuthError('Firebase Auth is not configured. Check your .env (VITE_FIREBASE_*) and enable Authentication in Firebase Console.')
      }
      setLoading(false)
    }
    return () => unsubscribe?.()
  }, [auth])

  const login = useCallback(
    async (email: string, password: string) => {
      if (!auth) throw new Error('Firebase Auth is not configured')
      await signInWithEmailAndPassword(auth, email, password)
    },
    [auth],
  )

  const logout = useCallback(async () => {
    if (!auth) return
    await signOut(auth)
    setUserProfile(null)
    setAuthPlaceId(null)
    clearPlaceStorage()
    clearAllCaches()
  }, [auth])

  const register = useCallback(
    async (input: RegisterInput) => {
      if (!auth) throw new Error('Firebase Auth is not configured')
      if (!useFirestore())
        throw new Error('Firestore must be enabled for registration')

      const { name, email, position, companyName, placeName, address, password } =
        input

      const userCred = await createUserWithEmailAndPassword(auth, email, password)
      const uid = userCred.user.uid

      const db = getFirestoreInstance()
      const companyId = doc(collection(db, 'companies')).id
      const placeId = doc(collection(db, 'places')).id

      await setDoc(doc(db, 'companies', companyId), {
        name: companyName,
        address: address || '',
        meta: {},
      })

      await setDoc(doc(db, 'places', placeId), {
        name: placeName,
        companyId,
        address: address || '',
        meta: {},
      })

      await setDoc(doc(db, 'places', placeId, 'members', uid), {
        name,
        email,
        position,
      })

      await setDoc(doc(db, 'users', uid), {
        defaultPlaceId: placeId,
        companyId,
        name,
        email,
        position,
      })

      await bootstrapPlace(placeId)

      setUserProfile({
        defaultPlaceId: placeId,
        companyId,
        name,
        email,
        position,
      })
      setAuthPlaceId(placeId)
      return placeId
    },
    [auth],
  )

  const value: AuthContextValue = {
    user,
    userProfile,
    loading,
    authError,
    login,
    logout,
    register,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
